import { h } from 'preact';
import { css } from '@emotion/react';
import { defaultCoinUrl } from '@/assets/coin';
import { useTranslation } from '@/hooks/useTransition';
import { useMemo } from 'preact/hooks';

const DynamicFontSizeText = ({
  text,
  initialFontSize,
  styles
}: {
  text: string;
  initialFontSize: number;
  styles?: string;
}) => {
  return (
    <span style={styles}>
      <span
        style={{
          fontSize: `${initialFontSize}px`,
          display: '-webkit-box',
          textAlign: 'left',
          WebkitBoxOrient: 'vertical',
          WebkitLineClamp: 2,
          width: 'auto',
          whiteSpace: 'normal',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          maxWidth: '100%'
        }}
      >
        {text}
      </span>
    </span>
  );
};

// 类型定义
interface CoinInfoProps {
  unlockNeedsAmount?: number;
  balanceAmount?: number;
  coinImage?: string;
  formatter?: (amount: number) => string;
  hideNeeds?: boolean;
  hideBalance?: boolean;
  className?: string;
  currency?: 'JOLI_COIN' | 'JOLI_GEM';
}

// 组件样式
const styles = {
  coinInfo: css`
    display: flex;
    width: calc(100% - 40px);
    max-width: 720px;
    justify-content: space-between;
  `,
  coinAmount: css`
    display: flex;
    flex-direction: row;
    gap: 4px;
    align-items: center;
    flex: 1;
    min-width: 0;
  `,
  coinAmountBalance: css`
    display: flex;
    flex-direction: row;
    gap: 4px;
    align-items: center;
    justify-content: flex-end;
    flex: 1;
    min-width: 0;
  `,
  coinLabelContainer: css`
    font-weight: 600;
    text-align: center;
    flex-shrink: 1;
    flex-grow: 0;
    min-width: 30px;
    overflow: hidden;
    color: #fff;
  `,
  coinValue: css`
    display: flex;
    align-items: center;
    gap: 3px;
    font-size: 22px;
    font-weight: 900;
    color: #fdde9d;
  `,
  coinIcon: css`
    width: 16px;
    height: 16px;
  `,
  gemIcon: css`
    width: 25px;
    height: 25px;
  `,
  autoExpandLabel: css`
    font-weight: 700;
    text-align: center;
    flex-shrink: 1;
    flex-grow: 0;
    min-width: 30px;
    overflow: hidden;
    width: auto;
    max-width: 70vw;
    display: inline-block;
    vertical-align: middle;
  `,
  coinValueText: css`
    font-weight: 600;
    text-align: center;
    flex-shrink: 1;
    flex-grow: 0;
    font-size: 16px;
    font-weight: 600;
    color: #d9d9d9;
  `
};

/**
 *
 * @param unlockNeedsAmount
 * @param balanceAmount
 * @param coinImage
 * @param formatter
 * @param hideNeeds
 * @param hideBalance
 * @param className
 */
export const CoinInfoDisplay: React.FC<CoinInfoProps> = ({
  unlockNeedsAmount = 0,
  balanceAmount = 0,
  coinImage = defaultCoinUrl,
  formatter = (amount) => (amount > 999 ? `999+` : `${amount}`),
  hideNeeds = false,
  hideBalance = false,
  currency = 'JOLI_COIN'
}) => {
  const displayBalanceAmount = formatter(balanceAmount);
  const { t } = useTranslation();
  const initialLabelFontSize = 14;

  const balanceKey = useMemo(() => {
    if (currency === 'JOLI_GEM') {
      return 'joligem.balance';
    }
    return 'jolicoin.balance';
  }, [currency]);

  return (
    <div style={styles.coinInfo.styles}>
      {!hideNeeds && (
        <div style={styles.coinAmount.styles}>
          <DynamicFontSizeText
            text={t('jolicoin.price')}
            initialFontSize={initialLabelFontSize}
            styles={styles.coinLabelContainer.styles}
          />
          <div style={styles.coinValue.styles}>
            <img
              src={coinImage}
              alt="Jolicoin"
              style={currency === 'JOLI_GEM' ? styles.gemIcon.styles : styles.coinIcon.styles}
            />
            <span>{unlockNeedsAmount}</span>
          </div>
        </div>
      )}

      {!hideBalance && (
        <div style={styles.coinAmountBalance.styles}>
          <DynamicFontSizeText
            text={t(balanceKey) + ':\u00A0' + displayBalanceAmount}
            initialFontSize={initialLabelFontSize}
            styles={styles.coinValueText.styles}
          />
        </div>
      )}
    </div>
  );
};
